import React, { useContext } from 'react';
import { GableType } from '../../models/GableType';
import { IInteriorProduct } from '../../models/IChainInfo';
import { IObj2d } from '../../models/IObj';
import { hasDimensions, hasProductLine } from '../../services/CabinetValidator';
import { IInteriorItem, InteriorProductType } from '../../services/WebApiClient';
import { DoorImageTop } from '../DoorEditor/DoorImageTop';
import { VerticalRuler } from '../DoorEditor/VerticalRuler';
import { CommonEditorProps, EditorContext } from '../EditorCommon/EditorContext';
import { InteriorDragContext } from './InteriorImageDragContext';

interface PulloutViolation {
    item: IInteriorItem,
    product: IInteriorProduct
}

const gableDisplayProps: React.SVGProps<SVGRectElement> = {
    fill: "none",
    stroke: "black",
    strokeWidth: 2
};


export function InteriorImageTop(props: {

}) {

    const ec = useContext(EditorContext);

    const dragContext = useContext(InteriorDragContext);

    if (!hasDimensions(ec.cabinet))
        throw new Error("dimensions not defined");
    if (!hasProductLine(ec.cabinet))
        throw new Error("product line not defined");

    const pulloutViolations: PulloutViolation[] = ec.cabinetService.interior.getPulloutViolations()
        .map(i => ({
            item: i,
            product: ec.cabinetService.interior.getProduct(i.productId)
        }));

    function selectPulloutViolator(evt: React.MouseEvent<SVGRectElement>, pv: PulloutViolation) {
        dragContext.setInfo({
            item: pv.item,
            initialCoords: { x: evt.clientX, y: evt.clientY },
            isDragging: false,
            mouseOffset: { x: 0, y: 0 },
            subGroup: pv.product.productSubGroup
        });
    }

    const gablesAndModules = ec.cabinet.interiorItems
        .map(ii => ({
            item: ii,
            product: ec.cabinetService.interior.getProduct(ii.productId)
        }))
        .filter(itemInfo =>
            (itemInfo.product.productType === InteriorProductType.Gable)
            || (itemInfo.product.productType === InteriorProductType.Module)
        );

    const gableDepth = gablesAndModules[0]?.product.defaultSize.z
        ?? ec.cabinet.depth! - 100;

    const wallThicknesses = InteriorImageTop.getWallThicknesses(ec);

    const gableInfo = ec.cabinetService.getGableInfo();

    return (
        <g>

            <VerticalRuler
                x={-VerticalRuler.width / 2 - wallThicknesses.left}
                y={0}
                length={ec.cabinet.depth}
            />

            <g className="walls">
                {wallThicknesses.left && (
                    <rect x={-wallThicknesses.left}
                        y={0}
                        width={wallThicknesses.left}
                        height={ec.cabinet.depth}
                        {...InteriorImageTop.wallDisplayProps}
                    />
                )}
                {wallThicknesses.right && (
                    <rect x={ec.cabinet.width}
                        y={0}
                        width={wallThicknesses.right}
                        height={ec.cabinet.depth}
                        {...InteriorImageTop.wallDisplayProps}
                    />
                )}
                <rect x={-wallThicknesses.left}
                    y={-InteriorImageTop.wallThickness}
                    width={ec.cabinet.width + wallThicknesses.left + wallThicknesses.right}
                    height={InteriorImageTop.wallThickness}
                    {...InteriorImageTop.wallDisplayProps}
                />
            </g>

            <g className="outer-gables">
                {[gableInfo.left, gableInfo.right].map((gable, idx) => (
                    <rect key={idx}
                        x={gable.position.x}
                        y={gable.position.z}
                        width={gable.size.x}
                        height={gable.size.z}
                        {...gableDisplayProps}
                    />
                ))}
            </g>

            {gablesAndModules.map(gam => (
                <rect key={gam.item.index}
                    width={gam.item.size.x}
                    height={gam.product.defaultSize.z}
                    x={gam.item.position.x}
                    y={0}
                    {...gableDisplayProps}
                />
            ))}
            {pulloutViolations.map(pv => (
                <g key={pv.item.index}>
                    <rect
                        width={pv.item.size.x}
                        height={gableDepth}
                        x={pv.item.position.x}
                        y={0}
                        fill="rgba(255,180,0,0.5)"
                        style={{ cursor: "pointer" }}
                        onClick={(evt) => selectPulloutViolator(evt, pv)}
                    />
                    <text x={pv.item.position.x + (pv.item.size.x / 2)}
                        y={gableDepth / 2}
                        textAnchor="middle"
                        fontSize={60}
                        style={{
                            color: "red",
                            whiteSpace: "pre-line",
                            pointerEvents: "none"
                        }}
                    >
                        {ec.translator("interior image top pullout warning text", "Begrænset\nudtræk!")}
                    </text>
                </g>
            ))}
            <DoorImageTop />
        </g>
    );
}


export module InteriorImageTop {

    export const wallThickness = 40;
    export const wallDisplayProps: React.SVGProps<SVGRectElement> = {
        fill: "#bbb",
        stroke: "none"
    };

    export function getWallThicknesses(ec: CommonEditorProps): { left: number, right: number } {

        const gi = ec.cabinetService.getGableInfo();
        const hasWallLeft = gi.left.type !== GableType.Gable;
        const hasWallRight = gi.right.type !== GableType.Gable;
        const wallThicknessLeft = hasWallLeft ? wallThickness : 0;
        const wallThicknessRight = hasWallRight ? wallThickness : 0;

        return {
            left: wallThicknessLeft,
            right: wallThicknessRight,
        };
    }

    export function boundingBox(ec: CommonEditorProps): IObj2d {

        const wallthicknesses = getWallThicknesses(ec);

        return {
            size: {
                x: ec.cabinet.width! + (wallthicknesses.left + wallthicknesses.right),
                y: ec.cabinet.depth! + wallThickness,
            },
            position: {
                x: -wallthicknesses.left,
                y: -wallThickness,
            }
        }
    }
}
